import { logger } from '@/lib/logger';
import React from 'react';
import { Linking, Modal, Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';

interface UpdateModalProps {
  visible: boolean;
  isMandatory: boolean;
  version: string;
  releaseNotes: string | null;
  onDismiss: () => void;
}

const APP_STORE_URL = 'https://apps.apple.com/app/id6752910402';
const PLAY_STORE_URL = 'market://details?id=com.openstudiojazz.josephine';

export const UpdateModal: React.FC<UpdateModalProps> = ({
  visible,
  isMandatory,
  version,
  releaseNotes,
  onDismiss,
}) => {
  const openStore = async () => {
    try {
      const url = Platform.OS === 'ios' ? APP_STORE_URL : PLAY_STORE_URL;
      const canOpen = await Linking.canOpenURL(url);
      
      if (canOpen) {
        await Linking.openURL(url);
        logger.info('[MOBILE] Opened store URL:', url);
      } else {
        logger.error('[MOBILE] Cannot open store URL:', url);
      }
    } catch (error) {
      logger.error('[MOBILE] Error opening store:', error);
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={isMandatory ? undefined : onDismiss}
    >
      <View style={styles.overlay}>
        <View style={styles.modal}>
          <Text style={styles.title}>
            {isMandatory ? 'Update Required' : 'Update Available'}
          </Text>

          <Text style={styles.version}>
            Version {version}
          </Text>

          {releaseNotes && (
            <View style={styles.releaseNotesContainer}>
              <Text style={styles.releaseNotesTitle}>What's New:</Text>
              <Text style={styles.releaseNotes}>{releaseNotes}</Text>
            </View>
          )}

          {isMandatory && (
            <Text style={styles.mandatoryText}>
              This update is required to continue using the app.
            </Text>
          )}

          <View style={styles.buttons}>
            {!isMandatory && (
              <TouchableOpacity
                style={[styles.button, styles.laterButton]}
                onPress={onDismiss}
              >
                <Text style={styles.laterButtonText}>Later</Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity
              style={[styles.button, styles.updateButton, isMandatory && styles.updateButtonFull]}
              onPress={openStore}
            >
              <Text style={styles.updateButtonText}>
                {isMandatory ? 'Update Now' : 'Update'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 24,
    width: '100%',
    maxWidth: 400,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1B1B1B',
    marginBottom: 8,
    textAlign: 'center',
  },
  version: {
    fontSize: 16,
    color: '#666',
    marginBottom: 16,
    textAlign: 'center',
  },
  releaseNotesContainer: {
    backgroundColor: '#F5F5F5',
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
  },
  releaseNotesTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1B1B1B',
    marginBottom: 8,
  },
  releaseNotes: {
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
  },
  mandatoryText: {
    fontSize: 14,
    color: '#F15A29',
    textAlign: 'center',
    marginBottom: 16,
    fontWeight: '500',
  },
  buttons: {
    flexDirection: 'row',
    gap: 12,
  },
  button: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  laterButton: {
    backgroundColor: '#F5F5F5',
  },
  laterButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#666',
  },
  updateButton: {
    backgroundColor: '#F15A29',
  },
  updateButtonFull: {
    flex: 1,
  },
  updateButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
});
